import { z } from 'zod';
import { isoDateSchema } from './common.js';

/**
 * Wave 14.4 — Boletim de Medição mensal.
 *
 * The "boletim de medição" is a monthly billing report Brazilian GCs
 * send to their clients. It rolls up every approved `medição` whose
 * period ends inside the target month, plus every purchase order issued
 * during the month (so the client sees committed spend alongside work
 * billed). The server does the aggregation; the web client renders the
 * PDF — this mirrors how we ship the RDO PDF (Wave 14.2).
 *
 * "month" is an ISO `YYYY-MM` string (local SP time on the API). We
 * resolve it to `periodStart=YYYY-MM-01` and `periodEnd=last-day-of-month`
 * server-side so the caller only has to think in months.
 */

/**
 * A `YYYY-MM` identifier. Rejects anything that doesn't match so a typo
 * ("2026-13") returns a 400 rather than silently querying a nonsense range.
 */
export const monthIdSchema = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'month must be YYYY-MM');

export const boletimMeasurementItemSchema = z.object({
  stageId: z.string(),
  stageName: z.string(),
  percentBilled: z.number().min(0).max(100),
  unitPriceCents: z.number().int().nonnegative(),
  subtotalCents: z.number().int().nonnegative(),
});
export type BoletimMeasurementItem = z.infer<typeof boletimMeasurementItemSchema>;

export const boletimMeasurementSchema = z.object({
  id: z.string(),
  periodStart: isoDateSchema,
  periodEnd: isoDateSchema,
  status: z.enum(['approved', 'sent', 'paid']),
  totalCents: z.number().int().nonnegative(),
  approvedAt: z.string().nullable(),
  approvedByName: z.string().nullable(),
  notes: z.string().nullable(),
  items: z.array(boletimMeasurementItemSchema),
});
export type BoletimMeasurement = z.infer<typeof boletimMeasurementSchema>;

export const boletimPurchaseOrderSchema = z.object({
  id: z.string(),
  code: z.string(),
  supplierName: z.string(),
  title: z.string(),
  status: z.enum(['issued', 'partially_received', 'received']),
  totalCents: z.number().int(),
  issuedAt: z.string(),
});
export type BoletimPurchaseOrder = z.infer<typeof boletimPurchaseOrderSchema>;

export const boletimPaymentSchema = z.object({
  id: z.string(),
  amountCents: z.number().int().nonnegative(),
  kind: z.enum(['po', 'measurement', 'reimbursement', 'refund', 'other']),
  method: z.enum(['pix', 'boleto', 'transfer', 'cash', 'other']),
  status: z.enum(['pending', 'settled', 'failed', 'cancelled']),
  occurredAt: z.string(),
  settledAt: z.string().nullable(),
  reference: z.string().nullable(),
});
export type BoletimPayment = z.infer<typeof boletimPaymentSchema>;

export const boletimTotalsSchema = z.object({
  measurementTotalCents: z.number().int().nonnegative(),
  poTotalCents: z.number().int().nonnegative(),
  paymentSettledCents: z.number().int().nonnegative(),
  /** Sum of measurements + POs — what the client contractually owes this month. */
  grandTotalCents: z.number().int().nonnegative(),
});
export type BoletimTotals = z.infer<typeof boletimTotalsSchema>;

export const boletimProjectSchema = z.object({
  id: z.string(),
  name: z.string(),
  address: z.string(),
  clientName: z.string(),
});
export type BoletimProject = z.infer<typeof boletimProjectSchema>;

export const boletimMedicaoSchema = z.object({
  project: boletimProjectSchema,
  month: monthIdSchema,
  periodStart: isoDateSchema,
  periodEnd: isoDateSchema,
  measurements: z.array(boletimMeasurementSchema),
  purchaseOrders: z.array(boletimPurchaseOrderSchema),
  payments: z.array(boletimPaymentSchema),
  totals: boletimTotalsSchema,
  generatedAt: z.string(),
});
export type BoletimMedicao = z.infer<typeof boletimMedicaoSchema>;
